import { FC } from 'react';
import { Edit3, Zap, Clock, Brain } from 'lucide-react';
import { GenerationMode } from '../types';

interface ModeSelectorProps {
  selectedMode: GenerationMode;
  onModeChange: (mode: GenerationMode) => void;
}

export const ModeSelector: FC<ModeSelectorProps> = ({ selectedMode, onModeChange }) => {
  return (
    <div className="mb-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Generation Mode</h2>
        <p className="text-gray-600">Select how you'd like to create your llm.txt file</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {/* Manual Mode */}
        <div
          className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200 ${
            selectedMode === 'manual'
              ? 'border-blue-500 bg-blue-50 shadow-lg'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
          }`}
          onClick={() => onModeChange('manual')}
        >
          <div className="flex items-start space-x-4">
            <div className={`flex-shrink-0 p-3 rounded-lg ${
              selectedMode === 'manual' ? 'bg-blue-100' : 'bg-gray-100'
            }`}>
              <Edit3 className={`h-6 w-6 ${
                selectedMode === 'manual' ? 'text-blue-600' : 'text-gray-600'
              }`} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Manual Mode</h3>
              <p className="text-gray-600 mb-4">
                Create your llm.txt file step-by-step with full control over every section and detail.
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-500">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>5-10 minutes</span>
                </div>
                <div className="text-sm text-gray-500">
                  ✓ Complete control over content<br />
                  ✓ No API keys required<br />
                  ✓ Perfect for detailed customization
                </div>
              </div>
            </div>
          </div>
          {selectedMode === 'manual' && (
            <div className="absolute top-4 right-4">
              <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
          )}
        </div>

        {/* Auto Mode */}
        <div
          className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200 ${
            selectedMode === 'auto'
              ? 'border-purple-500 bg-purple-50 shadow-lg'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
          }`}
          onClick={() => onModeChange('auto')}
        >
          <div className="flex items-start space-x-4">
            <div className={`flex-shrink-0 p-3 rounded-lg ${
              selectedMode === 'auto' ? 'bg-purple-100' : 'bg-gray-100'
            }`}>
              <Zap className={`h-6 w-6 ${
                selectedMode === 'auto' ? 'text-purple-600' : 'text-gray-600'
              }`} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Auto Mode
                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  AI-Powered
                </span>
              </h3>
              <p className="text-gray-600 mb-4">
                Let AI analyze your website and automatically generate a comprehensive llm.txt file.
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-500">
                  <Brain className="h-4 w-4 mr-2" />
                  <span>2-3 minutes</span>
                </div>
                <div className="text-sm text-gray-500">
                  ✓ AI-powered content analysis<br />
                  ✓ Automatic structure generation<br />
                  ✓ Review and edit before download
                </div>
              </div>
            </div>
          </div>
          {selectedMode === 'auto' && (
            <div className="absolute top-4 right-4">
              <div className="w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedMode === 'auto' && (
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg max-w-4xl mx-auto">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">!</span>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-medium text-amber-800 mb-1">BYOK (Bring Your Own Key)</h4>
              <p className="text-sm text-amber-700">
                Auto mode requires your own API key from OpenAI, Google Gemini, or OpenRouter. 
                Your keys are used securely for this session only and are never stored.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
